import { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  Button,
  TableContainer,
  IconButton,
  Box,
  TextField,
} from "@mui/material";
import { Switch } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import RichTextEditor from "../components/RichTextEditor";
import FormModal from "../components/FormModal";
import api from "../api/axios";
import { useNotify } from "../context/NotificationContext";
export default function Blogs() {
  const { notify } = useNotify();
  const [blogs, setBlogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [slug, setSlug] = useState("");

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadBlogs = () => api.get("/blogs").then((r) => setBlogs(r.data));

  useEffect(() => {
    loadBlogs();
  }, []);
  const formatSlug = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const openModal = (blog) => {
    setCurrent(blog);
    setTitle(blog?.title || "");
    setContent(blog?.content || "");
    setMetaTitle(blog?.meta_title || "");
    setMetaDescription(blog?.meta_description || "");
    setSlug(blog?.slug || "");
    setImage(null);
    setPreview(blog?.feature_image_url || null);
    setOpen(true);
  };

  const save = async () => {
    if (!title.trim() || !content.trim()) {
      notify("Title and Content are required", "error");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);
    formData.append("meta_title", metaTitle);
    formData.append("meta_description", metaDescription);
    formData.append("slug", slug);
    if (image) formData.append("feature_image", image);

    current
      ? await api.post(`/blogs/${current.id}?_method=PUT`, formData)
      : await api.post("/blogs", formData);

    setOpen(false);
    loadBlogs();
    current
      ? notify("Blog Updated Successfully")
      : notify("Blog Added Successfully");
  };

  const remove = async (id) => {
    await api.delete(`/blogs/${id}`);
    loadBlogs();
    notify("Blog Deleted Successfully")
  };

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h4" fontWeight={600}>
          Blogs
        </Typography>
        <Button variant="contained" onClick={() => openModal(null)}>
          Add Blog
        </Button>
      </Box>

      <Paper elevation={3} sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead
              sx={{
                background: "#111827",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <TableRow>
                <TableCell sx={{ color: "#ffffff" }}>
                  <b>Title</b>
                </TableCell>
                <TableCell sx={{ color: "#ffffff" }}>
                  <b>Status</b>
                </TableCell>
                <TableCell sx={{ color: "#ffffff" }}>
                  <b>Last Updated</b>
                </TableCell>
                <TableCell width={120} sx={{ color: "#ffffff" }}>
                  <b>Actions</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {blogs.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>{b.title}</TableCell>

                  <TableCell>
                    <Switch
                      checked={Boolean(b.published_at)}
                      onChange={() =>
                        api
                          .patch(`/blogs/${b.id}/toggle-publish`)
                          .then(loadBlogs,notify('Status Updated Successfully'))

                      }
                      color="success"
                    />
                  </TableCell>

                  <TableCell>
                    {b.updated_at
                      ? new Date(b.updated_at).toLocaleDateString()
                      : "-"}
                  </TableCell>

                  <TableCell>
                    <IconButton onClick={() => openModal(b)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => remove(b.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {blogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No blogs created yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal */}
      <FormModal
        open={open}
        title={current ? "Edit Blog" : "Add Blog"}
        onClose={() => setOpen(false)}
        onSubmit={save}
        maxWidth="md"
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Title</Typography>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 6,
              border: "1px solid #ccc",
            }}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Slug (URL)</Typography>
          <TextField
            fullWidth
            value={slug}
            onChange={(e) => setSlug(formatSlug(e.target.value))}
            placeholder="e.g. how-to-remap-your-car"
            helperText="Lowercase, hyphens only. Must be unique."
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Meta Title</Typography>
          <TextField
            fullWidth
            value={metaTitle}
            onChange={(e) => setMetaTitle(e.target.value)}
            placeholder="SEO title (optional)"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Meta Description</Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            helperText={`${metaDescription.length}/160`}
            placeholder="SEO description (max 160 characters)"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Feature Image</Typography>

          {preview && (
            <Box sx={{ mb: 1 }}>
              <img
                src={preview}
                alt="Preview"
                style={{
                  width: "100%",
                  maxHeight: 200,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            </Box>
          )}

          <Button
            component="label"
            startIcon={<UploadIcon />}
            variant="outlined"
          >
            Upload Image
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={(e) => {
                setImage(e.target.files[0]);
                setPreview(URL.createObjectURL(e.target.files[0]));
              }}
            />
          </Button>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Content
          </Typography>
          <RichTextEditor value={content} onChange={setContent} />
        </Box>
      </FormModal>
    </Container>
  );
}
